import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCustomerDetail } from "@/hooks/useCustomerDetail";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Package, MapPin, StickyNote } from "lucide-react";
import { formatDetailDate } from "@/lib/dateUtils";
import { formatParsedItemsPreview } from "@/lib/tableUtils";
import { formatGEL } from "@/lib/formatUtils";
import { OverlayHeader } from "@/components/layout/OverlayHeader";
import { OverlayContent } from "@/components/layout/OverlayContent";
import { OverlaySection, OverlaySummaryRow } from "@/components/layout/OverlaySection";
import { InlineSummaryRow } from "@/components/sheet";
import { PhoneDisplay } from "@/components/ui/PhoneDisplay";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import type { Order } from "@/types/order";

function OrderHistoryItem({ order, onClick, language }: { order: Order; onClick: (id: string) => void; language: "en" | "ka" }) {
  const dateSource = order.delivered_at || order.created_at;
  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5 border-b border-border/30 cursor-pointer hover:bg-muted/30 transition-colors rounded"
      onClick={() => onClick(order.id)}
      role="button"
      tabIndex={0}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">
          {order.order_number ? `#${order.order_number}` : formatParsedItemsPreview(order.parsed_items, { maxItems: 2 }) || "Order"}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <p className="text-xs text-muted-foreground">
            {dateSource ? formatDetailDate(dateSource, language) : ""}
          </p>
          <StatusBadge status={order.order_status} compact />
        </div>
      </div>
      {order.total_price != null && (
        <span className="text-sm font-semibold tabular-nums shrink-0">
          {formatGEL(Number(order.total_price))}
        </span>
      )}
    </div>
  );
}

export default function CustomerDetail() {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const { t, language } = useLanguage();
  const { customer, customerLoading, orders, ordersLoading } = useCustomerDetail(customerId);

  const validScopes = ["needs_attention", "active", "top", "all"];
  const handleClose = () => {
    if (from && validScopes.includes(from)) {
      navigate(`/customers?scope=${from}`);
    } else {
      navigate("/customers");
    }
  };

  const handleOrderClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const revenue = parseFloat(customer?.total_revenue || "0");
  const lastDate = customer?.last_delivered_at
    ? formatDetailDate(customer.last_delivered_at, language)
    : "—";

  const addresses = customer?.recent_addresses?.filter(Boolean) || [];
  const lastAddress = addresses[0] || null;
  const otherAddresses = addresses.slice(1);

  const title = customerLoading ? "..." : customer?.name || t("customers.notFound");

  return (
    <Sheet open={true} modal={true}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col"
        onPointerDownOutside={(e) => {
          e.preventDefault();
          handleClose();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleClose();
        }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <OverlayHeader
          title={title}
          breadcrumbs={[]}
          onClose={handleClose}
        />

        <OverlayContent isLoading={customerLoading}>
          {!customer ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              {t("customers.notFound")}
            </div>
          ) : (
            <div className="space-y-0">
              {/* Overview */}
              <OverlaySection title={t("customers.overview")}>
                {customer.phone && (
                  <OverlaySummaryRow label={t("customers.phone")}>
                    <PhoneDisplay phoneString={customer.phone} className="text-sm" />
                  </OverlaySummaryRow>
                )}
                <InlineSummaryRow
                  label={t("customers.stats.revenue")}
                  value={revenue > 0 ? formatGEL(revenue) : "—"}
                  emphasis
                />
                <InlineSummaryRow
                  label={t("customers.stats.orders")}
                  value={customer.delivered_count > 0 ? String(customer.delivered_count) : "—"}
                />
                <InlineSummaryRow
                  label={t("customers.stats.lastOrder")}
                  value={lastDate}
                />
              </OverlaySection>

              {/* Internal Note */}
              {customer.internal_note && (
                <OverlaySection
                  title={t("customers.internalNote")}
                  showBorder
                  icon={<StickyNote className="h-3.5 w-3.5" />}
                >
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {customer.internal_note}
                  </p>
                </OverlaySection>
              )}

              {/* Address */}
              {lastAddress && (
                <OverlaySection title={t("customers.lastAddress")} showBorder>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
                    <span>{lastAddress}</span>
                  </div>
                  {otherAddresses.length > 0 && (
                    <div className="pl-5 space-y-1">
                      <p className="text-xs text-muted-foreground/70">{t("customers.recentAddresses")}</p>
                      {otherAddresses.map((addr, i) => (
                        <p key={i} className="text-xs text-muted-foreground">{addr}</p>
                      ))}
                    </div>
                  )}
                </OverlaySection>
              )}

              {/* Order History */}
              <OverlaySection
                title={t("customers.orderHistory")}
                showBorder
                icon={<Package className="h-3.5 w-3.5" />}
              >
                {ordersLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-12" />
                    <Skeleton className="h-12" />
                    <Skeleton className="h-12" />
                  </div>
                ) : orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">{t("customers.noDeliveredOrders")}</p>
                ) : (
                  <div>
                    {orders.map((order) => (
                      <OrderHistoryItem
                        key={order.id}
                        order={order}
                        onClick={handleOrderClick}
                        language={language}
                      />
                    ))}
                  </div>
                )}
              </OverlaySection>
            </div>
          )}
        </OverlayContent>
      </SheetContent>
    </Sheet>
  );
}
